import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Job } from '../../types/job';
import { useAdminStore } from '../../stores/adminStore';
import { JobStatus } from '../../types/common';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';

interface UpdateJobStatusModalProps {
  job: Job;
  onClose: () => void;
  closeButtonLabel?: string;
  onSuccess?: () => void;
}

const UpdateJobStatusModal: React.FC<UpdateJobStatusModalProps> = ({
  job,
  onClose,
  closeButtonLabel,
  onSuccess,
}) => {
  const { t } = useTranslation('common');

  const [selectedJobStatus, setSelectedJobStatus] = useState<JobStatus>(
    (job.status as JobStatus) || 'active'
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 'expired' is deliberately excluded: it is system-derived only, set
  // automatically once a job's quote submission deadline passes.
  const jobStatuses: JobStatus[] = [
    'open',
    'in_progress',
    'completed',
    'cancelled',
    'paused',
    'reviewing_quotes',
  ];

  // 'expired' isn't one of the actionable jobStatuses above, so for an
  // expired job the <select>'s value wouldn't match any <option> -- render
  // it as an extra, disabled option so the dropdown shows the job's real
  // current state instead of silently falling back to whatever the browser
  // picks, and require the admin to actively choose a real target status
  // (see the Confirm button's disabled condition below) before submitting,
  // since admin_update_job_status rejects 'expired' outright.
  const isCurrentStatusActionable = jobStatuses.includes(job.status as JobStatus);
  const jobTitle: string = job.title;

  const { updateJobStatus } = useAdminStore();

  const isSelectionActionable = jobStatuses.includes(selectedJobStatus);

  const handleConfirm = async () => {
    if (!selectedJobStatus || !isSelectionActionable) return;

    try {
      setSaving(true);
      setError(null);
      await updateJobStatus(job.id, selectedJobStatus);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError(
        t('generic_error', { defaultValue: 'Une erreur est survenue. Veuillez réessayer.' })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <h2 className="text-2xl font-bold text-secondary mb-4">
        <Trans
          i18nKey={t('update_job_status', { job: jobTitle })}
          values={{ job: jobTitle }}
          components={{ bold: <span style={{ fontWeight: 'bold', color: '#007475' }} /> }}
        />
      </h2>

      <div className="flex flex-col gap-3">
        <label htmlFor="user-status" className="text-sm font-semibold text-gray-700">
          {t('select_status')}
        </label>
        <select
          id="user-status"
          value={selectedJobStatus}
          onChange={e => setSelectedJobStatus(e.target.value as JobStatus)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {!isCurrentStatusActionable && (
            <option value={job.status} disabled>
              {t(`job_statuses.${job.status}`)}
            </option>
          )}
          {jobStatuses.map(status => (
            <option key={status} value={status}>
              {t(`job_statuses.${status}`)}
            </option>
          ))}
        </select>
        {error && <div className="text-sm font-semibold text-red-600">{error}</div>}
      </div>

      <div className="flex flex-row md:flex-row justify-end gap-4 mt-8">
        <Button
          variant="simple-text"
          onClick={onClose}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
          disabled={saving}
        >
          {closeButtonLabel || t('common:form_actions.cancel')}
        </Button>

        <Button
          variant="primary"
          onClick={handleConfirm}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
          disabled={saving || !isSelectionActionable}
        >
          {saving
            ? t('loading', { ns: 'common', defaultValue: 'Chargement...' })
            : t('common:form_actions.confirm', { ns: 'common' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default UpdateJobStatusModal;
